import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Lines, SectionTag } from "../components/site/Motion";
import { IMG } from "../lib/images";

const CHAPTERS = [
  {
    id: "oneplus",
    tag: "ONE+1™",
    title: ["Two seats.", "One line."],
    text: "L'architettura tandem ONE+1 posiziona guidatore e passeggero uno dietro l'altro. Il risultato: una carrozzeria larga appena 1009 mm, due posti veri, un ingombro urbano minimo.",
    specs: [["Larghezza", "1009 mm"], ["Lunghezza", "2529 mm"], ["Altezza", "1424 mm"], ["Passo", "1730 mm"]],
    img: "/assets/oneplus-cockpit.webp",
    alt: "Abitacolo tandem ONE+1 visto dall'alto con linea centrale gialla",
  },
  {
    id: "ftouch",
    tag: "F-TOUCH™",
    title: ["Parking at", "your fingertips."],
    text: "F-TOUCH è il sistema di movimentazione a bassa velocità di SWAPA: comandi fisici integrati nella carrozzeria permettono di muovere SWAPA avanti e indietro dall'esterno, per gestire dall'esterno anche il parcheggio più stretto. Non è guida autonoma: è il controllo del parcheggio, a portata di dito.",
    specs: [["Attivazione", "Comandi esterni"], ["Direzione", "Avanti / indietro"], ["Velocità", "Ridotta"], ["Uso", "Parcheggi stretti"]],
    video: true,
    alt: "F-TOUCH in azione in un parcheggio stretto",
  },
  {
    id: "monoq",
    tag: "MONOQ™",
    title: ["Built like a car.", "Sized for the city."],
    text: "MONOQ è l'architettura strutturale di SWAPA: una monoscocca in alluminio e acciaio con stadi di assorbimento differenziati, che porta principi costruttivi automobilistici nelle proporzioni di un quadriciclo L6e.",
    specs: [["Struttura", "Monoscocca"], ["Materiali", "Alluminio / acciaio"], ["Filosofia", "Automotive"], ["Formato", "L6e"]],
    img: "/assets/monoq-exploded.webp",
    alt: "Struttura monoscocca MONOQ esplosa con pannelli e rinforzi",
  },
];

export default function TechnologyPage() {
  useEffect(() => {
    document.title = "Tecnologia SWAPA — ONE+1™, F-TOUCH™, MONOQ™ | SWAPA";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "La tecnologia di SWAPA ZIP: monoscocca MONOQ in alluminio e acciaio, abitacolo tandem ONE+1, controlli esterni F-TOUCH e batterie rimovibili con ricarica domestica.");
  }, []);

  return (
    <div data-testid="technology-page" className="bg-ink pt-32">
      <header className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Tecnologia proprietaria</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={["Automotive", "inside."]} className="text-5xl text-white sm:text-7xl lg:text-8xl" />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400">
            Tre tecnologie firmano il carattere di SWAPA: l'abitacolo tandem ONE+1, i comandi di
            parcheggio esterni F-TOUCH e la struttura monoscocca MONOQ.
          </p>
        </Reveal>
      </header>

      <section data-testid="tech-360-video" className="relative mt-16 overflow-hidden border-y border-white/10">
        <video
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          preload="metadata"
          poster="/assets/tech-360-poster.jpg"
          aria-label="SWAPA ripresa a 360 gradi"
          className="h-[55vh] w-full object-cover sm:h-[78vh]"
        >
          <source src="/assets/tech-360.webm" type="video/webm" />
          <source src="/assets/tech-360.mp4" type="video/mp4" />
        </video>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/40" aria-hidden="true" />
        <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 sm:left-10">
          SWAPA 360° — Design in movimento
        </p>
      </section>

      {CHAPTERS.map((c, i) => (
        <section key={c.id} id={c.id} data-testid={`tech-${c.id}`} className="border-t border-white/10 py-24 sm:py-32">
          <div className={`mx-auto grid max-w-[1600px] items-center gap-14 px-5 sm:px-10 lg:grid-cols-2`}>
            <div className={i % 2 === 1 ? "lg:order-2" : ""}>
              <SectionTag index={`0${i + 1}`} label={c.tag} />
              <h2 className="mt-6 font-display font-extrabold uppercase leading-[0.92] tracking-tight">
                <Lines lines={c.title} className="text-3xl text-white sm:text-5xl lg:text-6xl" />
              </h2>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">{c.text}</p>
              </Reveal>
              <div className="mt-10 grid grid-cols-2 gap-px bg-white/10">
                {c.specs.map(([k, v]) => (
                  <div key={k} className="bg-ink p-4">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{k}</p>
                    <p className="mt-1 font-mono text-sm text-white">{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <Reveal y={70} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <div className="overflow-hidden border border-white/10">
                {c.video ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster="/assets/ftouch-poster.jpg"
                    aria-label={c.alt}
                    className="aspect-[4/3] w-full object-cover"
                  >
                    <source src="/assets/ftouch.webm" type="video/webm" />
                    <source src="/assets/ftouch.mp4" type="video/mp4" />
                  </video>
                ) : (
                  <img src={c.img} alt={c.alt} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                )}
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="border-t border-white/10 py-24 text-center">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">La tecnologia si capisce guidando</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link to="/test-drive" data-testid="tech-testdrive-cta" className="group inline-flex items-center gap-3 bg-swapa-yellow px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5">
              Prenota un test drive <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/preordina" data-testid="tech-preorder-cta" className="inline-flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white hover:border-swapa-yellow hover:text-swapa-yellow">
              Pre-ordina — €200
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
